import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  useGetBankAccountsQuery,
  useGetChequebookQuery,
  useCreateChequebookMutation,
  useUpdateChequebookMutation,
} from '../../../store/api/financeApi';

function ChequebookForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new' || id === undefined;
  const { data: book, isLoading } = useGetChequebookQuery(id, { skip: isNew });
  const { data: bankAccounts = [] } = useGetBankAccountsQuery();
  const [createChequebook] = useCreateChequebookMutation();
  const [updateChequebook] = useUpdateChequebookMutation();

  const [form, setForm] = useState({
    bankAccountId: '',
    name: '',
    startLeaf: '',
    numberOfLeaves: '',
    status: 'active',
  });

  useEffect(() => {
    if (book) {
      const start = book.startLeaf != null ? Number(book.startLeaf) : 0;
      const end = book.endLeaf != null ? Number(book.endLeaf) : 0;
      const leaves = end >= start ? end - start + 1 : 0;
      setForm({
        bankAccountId: book.bankAccountId || '',
        name: book.name || '',
        startLeaf: book.startLeaf != null ? String(book.startLeaf) : '',
        numberOfLeaves: leaves > 0 ? String(leaves) : '',
        status: book.status || 'active',
      });
    }
  }, [book]);

  const startNum = parseInt(form.startLeaf, 10);
  const numLeaves = parseInt(form.numberOfLeaves, 10);
  const endLeaf = Number.isInteger(startNum) && Number.isInteger(numLeaves) && numLeaves >= 1
    ? startNum + numLeaves - 1
    : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.bankAccountId || !form.name?.trim()) {
      alert('Bank account and name are required.');
      return;
    }
    const start = parseInt(form.startLeaf, 10);
    const leaves = parseInt(form.numberOfLeaves, 10);
    if (!Number.isInteger(start) || start < 0) {
      alert('Starting serial number must be a whole number (0 or more).');
      return;
    }
    if (!Number.isInteger(leaves) || leaves < 1) {
      alert('Number of leaves must be at least 1.');
      return;
    }
    const end = start + leaves - 1;
    try {
      if (isNew) {
        await createChequebook({
          bankAccountId: form.bankAccountId,
          name: form.name.trim(),
          startLeaf: start,
          endLeaf: end,
        }).unwrap();
        navigate('/finance/chequebooks');
      } else {
        await updateChequebook({
          id,
          name: form.name.trim(),
          status: form.status,
        }).unwrap();
        navigate('/finance/chequebooks');
      }
    } catch (err) {
      alert(err?.data?.message || 'Error saving');
    }
  };

  if (!isNew && isLoading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/finance/chequebooks')} sx={{ mb: 2 }}>
        Back
      </Button>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 3 }}>
        {isNew ? 'Add Chequebook' : 'Edit Chequebook'}
      </Typography>
      <Card sx={{ maxWidth: 560 }}>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              select
              label="Bank account"
              value={form.bankAccountId}
              onChange={(e) => setForm((p) => ({ ...p, bankAccountId: e.target.value }))}
              required
              disabled={!isNew}
              sx={{ mb: 2 }}
            >
              <MenuItem value="">Select</MenuItem>
              {bankAccounts.filter((b) => b.isActive).map((b) => (
                <MenuItem key={b.id} value={b.id}>{b.name} {b.accountNumber ? `(${b.accountNumber})` : ''}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Chequebook name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="e.g. Book 1, Leaves 1001-1100"
              required
              disabled={!isNew}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="number"
              inputProps={{ min: 0, step: 1 }}
              label="Starting serial number"
              value={form.startLeaf}
              onChange={(e) => setForm((p) => ({ ...p, startLeaf: e.target.value }))}
              required
              disabled={!isNew}
              helperText="First cheque number in the book, e.g. 1 or 1001"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="number"
              inputProps={{ min: 1, step: 1 }}
              label="Number of leaves"
              value={form.numberOfLeaves}
              onChange={(e) => setForm((p) => ({ ...p, numberOfLeaves: e.target.value }))}
              required
              disabled={!isNew}
              helperText={endLeaf != null ? `End number will be: ${endLeaf}` : 'Total leaves in this book'}
              sx={{ mb: 2 }}
            />
            {endLeaf != null && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Range: {form.startLeaf} to {endLeaf} ({form.numberOfLeaves} leaves)
              </Typography>
            )}
            {!isNew && (
              <TextField
                fullWidth
                select
                label="Status"
                value={form.status}
                onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                sx={{ mb: 2 }}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </TextField>
            )}
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button type="submit" variant="contained">
                {isNew ? 'Create' : 'Save'}
              </Button>
              <Button onClick={() => navigate('/finance/chequebooks')}>Cancel</Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default ChequebookForm;
